import { useCallback } from 'react'
import { useQueryClient } from 'react-query'
import {
  FileObject,
  getFileFromFileObjects,
  useMutationUploadImages
} from '@plandi/common'
import { components } from '@plandi/common/src/generated'

import { projectKeys } from './projects.constants'
import { useDispatch } from 'react-redux'
import { LoadinProgress } from 'src/reducers/actions/LoadingActions'

type Options = {
  projectId?: number
  images?: FileObject[]
  cover?: FileObject
}

export const useUploadProjectImages = () => {
  const queryClient = useQueryClient()
  const mutationUploadImages = useMutationUploadImages()
  const dispatch = useDispatch()

  const onUploadProgress = useCallback(
    (progressEvent: { loaded: number; total: number }) => {
      const percentage = Math.floor(
        (progressEvent.loaded * 100) / progressEvent.total
      )
      dispatch(LoadinProgress(percentage))
    },
    [dispatch]
  )
  return useCallback(
    async (options?: Options) => {
      const project = options?.projectId
        ? queryClient.getQueryData<
            components['schemas']['ProjectMarketResource']
          >(projectKeys.detailMe(options.projectId))
        : {}

      const alreadyUploadedProjectImages =
        (project?.images ?? [])?.filter((uploadedImage) =>
          options?.images?.some((image) => image.data === uploadedImage.url)
        ) ?? []

      const filesFromImagesForm = getFileFromFileObjects(options?.images)

      const uploadedImages = await mutationUploadImages.mutateAsync({
        files: options?.cover?.file
          ? [options.cover.file, ...filesFromImagesForm]
          : filesFromImagesForm,
        options: {
          isWatermark: true,
          isMarketProjectImage: true,
          onUploadProgress
        }
      })

      const [cover, ...images] = options?.cover?.file
        ? uploadedImages
        : [options?.cover ? project?.cover : null, ...uploadedImages]

      const imagesId = [...images, ...alreadyUploadedProjectImages]
        .filter((image): image is { id: number } => Boolean(image?.id))
        .map((image) => image.id)
        .sort((a, b) => (a > b ? 1 : -1))

      return {
        coverId: cover?.id,
        imagesId
      }
    },
    [queryClient, mutationUploadImages, onUploadProgress]
  )
}
